<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { format } from 'date-fns';
import fromUnixTime from 'date-fns/fromUnixTime';
import { useI18n } from 'vue-i18n';
import { useSLADeadline } from 'dashboard/composables/useSLADeadline';
import { useSLAStatuses } from 'dashboard/composables/useSLAStatuses';
import SLAPopoverCard from './SLAPopoverCard.vue';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showExtendedInfo: {
    type: Boolean,
    default: false,
  },
  parentWidth: {
    type: Number,
    default: 1000,
  },
});

const REFRESH_INTERVAL = 60000;
const { t } = useI18n();

const timer = ref(null);
const slaStatuses = ref([]);
const showDeadlineFor = ref({});

const DEADLINE_FIELD_MAP = {
  FRT: 'sla_frt_deadline_at',
  NRT: 'sla_nrt_deadline_at',
  RT: 'sla_rt_deadline_at',
};

const appliedSLA = computed(() => props.chat?.applied_sla);
const slaEvents = computed(() => props.chat?.sla_events);

const hasSlaThreshold = computed(() => slaStatuses.value.length > 0);

const showSlaPopoverCard = computed(
  () => props.showExtendedInfo && slaEvents.value?.length > 0
);

const groupClass = computed(() => {
  return props.showExtendedInfo
    ? 'h-[26px] rounded-lg bg-n-alpha-1'
    : 'rounded h-5  border border-n-strong';
});

const getDeadlineText = type => {
  const ts = appliedSLA.value?.[DEADLINE_FIELD_MAP[type]];
  if (!ts) return null;
  return format(fromUnixTime(ts), 'MMM dd, HH:mm');
};

const toggleDeadline = type => {
  if (getDeadlineText(type)) {
    showDeadlineFor.value = {
      ...showDeadlineFor.value,
      [type]: !showDeadlineFor.value[type],
    };
  }
};

const getStatusLabel = status => {
  const statusKey = status.isSlaMissed ? 'MISSED' : 'DUE';
  return t(`CONVERSATION.HEADER.SLA_STATUS.${status.type}`, {
    status: t(`CONVERSATION.HEADER.SLA_STATUS.${statusKey}`),
  });
};

const getThresholdText = status => {
  const deadline = getDeadlineText(status.type);
  if (showDeadlineFor.value[status.type] && deadline) return deadline;
  return status.threshold;
};

const SLA_TYPE_COLORS = {
  FRT: 'text-n-blue-11',
  NRT: 'text-n-violet-11',
  RT: 'text-n-teal-11',
};

const getTextStyles = (isSlaMissed, type) =>
  isSlaMissed ? 'text-n-ruby-11' : (SLA_TYPE_COLORS[type] ?? 'text-n-slate-11');

const updateSlaStatuses = () => {
  slaStatuses.value = useSLAStatuses(
    useSLADeadline(appliedSLA.value, props.chat?.waiting_since),
    props.chat
  );
};

const createTimer = () => {
  timer.value = setTimeout(() => {
    updateSlaStatuses();
    createTimer();
  }, REFRESH_INTERVAL);
};

watch(
  () => props.chat,
  () => {
    updateSlaStatuses();
  }
);

onMounted(() => {
  updateSlaStatuses();
  createTimer();
});

onUnmounted(() => {
  if (timer.value) {
    clearTimeout(timer.value);
  }
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="hasSlaThreshold"
    class="relative flex items-center cursor-pointer min-w-fit group"
    :class="groupClass"
  >
    <div class="flex items-center w-full truncate px-1.5 gap-1">
      <template v-for="(status, index) in slaStatuses" :key="status.type">
        <div
          v-if="index > 0"
          class="w-px h-3 rounded-sm bg-n-strong flex-shrink-0"
        />
        <div class="flex items-center gap-1">
          <fluent-icon
            size="12"
            :icon="status.icon"
            type="outline"
            :icon-lib="status.isSlaMissed ? 'lucide' : 'fluent'"
            class="flex-shrink-0"
            :class="getTextStyles(status.isSlaMissed, status.type)"
          />
          <span
            v-if="showExtendedInfo && parentWidth > 650"
            class="text-xs font-medium"
            :class="getTextStyles(status.isSlaMissed, status.type)"
          >
            {{ getStatusLabel(status) }}
          </span>
          <span
            v-else
            class="text-xs font-medium"
            :class="getTextStyles(status.isSlaMissed, status.type)"
          >
            {{ status.type }}
          </span>
          <span
            class="text-xs font-medium"
            :class="[
              getTextStyles(status.isSlaMissed, status.type),
              showExtendedInfo && 'ltr:pl-0.5 rtl:pr-0.5',
              getDeadlineText(status.type) ? 'cursor-pointer' : '',
            ]"
            @click="toggleDeadline(status.type)"
          >
            {{ getThresholdText(status) }}
          </span>
        </div>
      </template>
    </div>
    <SLAPopoverCard
      v-if="showSlaPopoverCard"
      :sla-missed-events="slaEvents"
      class="start-0 xl:start-auto xl:end-0 top-7 hidden group-hover:flex"
    />
  </div>
</template>
